import type { FieldOf, PollOption, PollVote } from "@fobu/schema";

type PollField = FieldOf<"poll">;

const WEEKDAY_DE = ["So", "Mo", "Di", "Mi", "Do", "Fr", "Sa"];
const WEEKDAY_EN = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTH_DE = ["Jan.", "Feb.", "März", "Apr.", "Mai", "Juni", "Juli", "Aug.", "Sep.", "Okt.", "Nov.", "Dez."];
const MONTH_EN = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** Teile eines Termins zur Anzeige (z. B. Kachel „Mo · 12 · Okt.“). Rechnet ohne Zeitzone (Kalendertag). */
export function pollOptionParts(o: PollOption, lang = "de") {
  const en = lang.startsWith("en");
  const m = o.date ? /^(\d{4})-(\d{2})-(\d{2})$/.exec(o.date) : null;
  const d = m ? new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]))) : null;
  const time = o.start ? (o.end ? `${o.start}–${o.end}` : o.start) : "";
  return {
    weekday: d ? (en ? WEEKDAY_EN : WEEKDAY_DE)[d.getUTCDay()]! : "",
    day: d ? String(d.getUTCDate()) : "",
    month: d ? (en ? MONTH_EN : MONTH_DE)[d.getUTCMonth()]! : "",
    year: m ? m[1]! : "",
    time,
    label: o.label,
  };
}

/** „Mo, 12.10.2026, 18:00–20:00 · Online“ (ohne Monatsnamen – bleibt klein im Formular-Bundle). */
export function formatPollOption(o: PollOption, lang = "de"): string {
  const en = lang.startsWith("en");
  const m = o.date ? /^(\d{4})-(\d{2})-(\d{2})$/.exec(o.date) : null;
  const wd = m ? (en ? WEEKDAY_EN : WEEKDAY_DE)[new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]))).getUTCDay()] : "";
  const date = m ? `${wd}, ${en ? `${m[1]}-${m[2]}-${m[3]}` : `${m[3]}.${m[2]}.${m[1]}`}` : "";
  const t = o.start ? (o.end ? `${o.start}–${o.end}` : o.start) : "";
  const time = t ? `${t}${en ? "" : " Uhr"}` : "";
  return [[date, time].filter(Boolean).join(", "), o.label].filter(Boolean).join(" · ") || "—";
}

export const POLL_VOTE_LABEL: Record<PollVote, string> = { yes: "ja", maybe: "vielleicht", no: "nein" };

/** Antwort lesen: nur gültige Einträge, fehlende Termine zählen als „nein“. */
export function pollVotes(field: PollField, value: unknown): Record<string, PollVote> {
  const out: Record<string, PollVote> = {};
  if (!value || typeof value !== "object" || Array.isArray(value)) return out;
  for (const o of field.options) {
    const v = (value as Record<string, unknown>)[o.id];
    if (v === "yes" || v === "no" || (v === "maybe" && field.allowMaybe)) out[o.id] = v;
  }
  return out;
}

export interface PollTallyRow {
  id: string;
  option: PollOption;
  label: string;
  yes: number;
  maybe: number;
  no: number;
  /** ja = 1, vielleicht = ½ – zum Sortieren und für „bester Termin“. */
  score: number;
}

/** Stimmen je Termin; `best`: Termine mit der höchsten Punktzahl (bei Gleichstand mehrere). */
export function pollTally(field: PollField, values: unknown[], lang = "de") {
  const rows: PollTallyRow[] = field.options.map((o) => ({ id: o.id, option: o, label: formatPollOption(o, lang), yes: 0, maybe: 0, no: 0, score: 0 }));
  let answered = 0;
  for (const v of values) {
    const votes = pollVotes(field, v);
    if (!Object.keys(votes).length) continue;
    answered++;
    for (const r of rows) {
      const x = votes[r.id] ?? "no";
      r[x]++;
    }
  }
  for (const r of rows) r.score = r.yes + r.maybe / 2;
  const top = Math.max(0, ...rows.map((r) => r.score));
  const best = top > 0 ? rows.filter((r) => r.score === top).map((r) => r.id) : [];
  return { answered, rows, best };
}
